import find from 'latest_modules/lodash/find';
import includes from 'latest_modules/lodash/includes';
import merge from 'latest_modules/lodash/merge';
import pick from 'latest_modules/lodash/pick';
import Vue from 'vue';
import Shipping from '../schema/Shipping';
import { SERVICES } from '../../constants/service';

import shippingsApi from '../../api/shippings';
import { SHIPPINGS as shippingsTypes } from '../mutation-types';

// State
const state = merge(Shipping.state, {
  service: SERVICES.SHOP
});

// Getters
const getters = merge(Shipping.getters, {
  current(state) {
    return find(state.all, (s) => includes(s.is_default_for, state.service));
  },

  formatOneLine() {
    return Shipping.formatOneLine;
  }
});

// Actions
const actions = {
  /*
  使用するサービスを選択する
  */
  setService({ commit }, service) {
    commit(shippingsTypes.SERVICE_CHANGE, service);
    return Promise.resolve(service);
  },

  /*
  登録されている配送先住所を取得する
  */
  fetchAll({ commit }) {
    return new Promise(function (resolve, reject) {
      shippingsApi.getAll(function (err, res) {
        if (err) {
          return reject(err);
        }
        // 一度データを空にする
        commit(shippingsTypes.CLEAR);
        for (let shipping of res.shippings) {
          commit(shippingsTypes.ADD, shipping);
        }
        resolve();
      });
    });
  },

  /*
  現在デフォルトに指定されている配送先住所を取得する
  */
  fetchCurrent({ commit, state, getters }) {
    return new Promise(function (resolve, reject) {
      shippingsApi.getCurrent(
        {
          service: state.service
        },
        function (err, res) {
          if (err) {
            return reject(err);
          }
          if (res.shipping) {
            // すでにデータが存在すれば先に削除する
            if (getters.findById(res.shipping._id)) {
              commit(shippingsTypes.REMOVE, {
                _id: res.shipping._id
              });
            }
            commit(shippingsTypes.ADD, res.shipping);
          }
          resolve();
        }
      );
    });
  },

  /*
  配送先住所を新規追加する
  @param {String} payload._id Shipping id（更新時のみ渡される）
  @param {String} payload.name 配送先氏名
  @param {String} payload.country 国コード
  @param {String} payload.zip ZIPコード
  @param {String} payload.state State/Province
  @param {String} payload.city City
  @param {String} payload.line1 Address line 1
  @param {String} payload.line2 Address line 2
  @param {String} payload.tel Telephone number
  @param {String} payload.user User id
  @param {String} payload.label 配送先名
  */
  add({ commit }, payload) {
    return new Promise(function (resolve, reject) {
      const params = pick(payload, [
        '_id',
        'name',
        'country',
        'zip',
        'state',
        'city',
        'line1',
        'line2',
        'tel',
        'user',
        'label',
        'tax_code',
        'temporary'
      ]);
      shippingsApi.post(params, function (err, res) {
        if (err) {
          return reject(err);
        }
        commit(shippingsTypes.ADD, res.shipping);
        resolve(res.shipping);
      });
    });
  },

  /*
  登録されている配送先住所を削除する
  @param {String} payload._id Shipping id
  @param {String} payload.wishlist_id Wish list id
  */
  remove({ commit }, payload) {
    return new Promise(function (resolve, reject) {
      const params = {
        id: payload._id,
        wishlist_id: payload.wishlist_id
      };

      if (!params.id) {
        return reject(new Error('No shipping specified.'));
      }

      shippingsApi.delete(params, function (err) {
        if (err) {
          return reject(err);
        }
        commit(shippingsTypes.REMOVE, {
          _id: payload._id
        });
        resolve();
      });
    });
  },

  /*
  使用する配送先を変更する (defaultFor を設定する)
  @param {String} payload._id Shipping id
  */
  changeCurrent({ commit, state }, payload) {
    return new Promise(function (resolve, reject) {
      const params = {
        id: payload._id,
        service: state.service
      };

      if (!params.id) {
        return reject(new Error('No shipping specified.'));
      }
      if (!params.service) {
        return reject(new Error('No service specified.'));
      }

      shippingsApi.postCurrent(params, function (err) {
        if (err) {
          return reject(err);
        }
        commit(shippingsTypes.CURRENT_CHANGE, {
          _id: params.id
        });
        resolve(state.all[params.id]);
      });
    });
  }
};

// Mutations
const mutations = {
  [shippingsTypes.SERVICE_CHANGE](state, service) {
    state.service = service;
  },

  [shippingsTypes.CLEAR](state) {
    Vue.set(state, 'all', {});
    Vue.set(state, 'allList', []);
  },

  [shippingsTypes.ADD](state, payload) {
    const shipping = new Shipping(payload).toObject();
    Vue.set(state.all, shipping._id, shipping);
    state.allList.push(shipping._id);
  },

  [shippingsTypes.REMOVE](state, payload) {
    Vue.delete(state.allList, state.allList.indexOf(payload._id));
    Vue.delete(state.all, payload._id);
  },

  [shippingsTypes.CURRENT_CHANGE](state, payload) {
    return state.allList.map(function (id) {
      const ship = state.all[id];
      if (includes(ship.is_default_for, state.service)) {
        // _.pull を使用すると current のリアクティブが検出されない
        Vue.delete(
          ship.is_default_for,
          ship.is_default_for.indexOf(state.service)
        );
      }
      if (ship._id === payload._id) {
        ship.is_default_for.push(state.service);
      }
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
